import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Checkbox } from '../ui/checkbox';
import { toast } from '../../hooks/use-toast';
import { BACKEND_URL } from '../../config/canonical';
import { 
  ChevronRight, ChevronDown, Plus, X, Zap, FileText,
  User, Building, Calendar, AlertTriangle, MapPin, Shield, StickyNote, Car, Trash2
} from 'lucide-react';
import CountyAssignmentPicker from '../CountyAssignmentPicker';

// Case Types with specific field configurations
const CASE_TYPES = [
  { value: 'SURVEILLANCE', label: 'Surveillance', hasSpecificFields: true },
  { value: 'BACKGROUND_CHECK', label: 'Background Check', hasSpecificFields: true },
  { value: 'MISSING_PERSON', label: 'Missing Person', hasSpecificFields: true },
  { value: 'BAIL_BOND_APPREHENSION', label: 'Bail Bond / Apprehension', hasSpecificFields: true },
  { value: 'INFIDELITY', label: 'Infidelity / Domestic', hasSpecificFields: true },
  { value: 'CHILD_CUSTODY', label: 'Child Custody', hasSpecificFields: false },
  { value: 'WORKERS_COMP', label: 'Workers Compensation', hasSpecificFields: true },
  { value: 'INSURANCE_FRAUD', label: 'Insurance Fraud', hasSpecificFields: true },
  { value: 'CRIMINAL_DEFENSE', label: 'Criminal Defense Support', hasSpecificFields: true },
  { value: 'ASSET_SEARCH', label: 'Asset Search', hasSpecificFields: false },
  { value: 'SKIP_TRACE', label: 'Skip Trace', hasSpecificFields: true },
  { value: 'STOLEN_PROPERTY', label: 'Stolen Property', hasSpecificFields: false },
  { value: 'DOMESTIC_VIOLENCE', label: 'Domestic Violence', hasSpecificFields: false },
  // MASTER DIRECTIVE #23 — Vehicle Repossession
  { value: 'VEHICLE_REPOSSESSION', label: 'Vehicle Repossession', hasSpecificFields: true },
  { value: 'OTHER', label: 'Other', hasSpecificFields: false },
];

const CASE_SUB_TYPES = {
  SURVEILLANCE: ['Covert', 'Counter-Surveillance', 'Mobile', 'Stationary', 'Technical'],
  BACKGROUND_CHECK: ['Employment', 'Pre-Employment', 'Tenant', 'Personal', 'Business Due Diligence'],
  MISSING_PERSON: ['Runaway', 'Abduction', 'Voluntary', 'Dementia/Alzheimer', 'Endangered'],
  BAIL_BOND_APPREHENSION: ['Failure to Appear', 'Bond Forfeiture', 'Fugitive Recovery'],
  WORKERS_COMP: ['Injury Verification', 'Activity Check', 'Employer Fraud'],
  INSURANCE_FRAUD: ['Auto', 'Property', 'Health', 'Life', 'Disability'],
  CRIMINAL_DEFENSE: ['Witness Location', 'Evidence Review', 'Scene Documentation'],
  VEHICLE_REPOSSESSION: ['Voluntary Repo', 'Involuntary Repo', 'Skip Repo', 'Impound Recovery', 'Collateral Verification'],
};

const PRIORITY_OPTIONS = [
  { value: 'LOW', label: 'Low', color: 'bg-slate-100 text-slate-600' },
  { value: 'NORMAL', label: 'Normal', color: 'bg-blue-100 text-blue-700' },
  { value: 'HIGH', label: 'High', color: 'bg-orange-100 text-orange-700' },
  { value: 'URGENT', label: 'Urgent', color: 'bg-red-100 text-red-700' },
];

const RISK_LEVELS = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];
const CONFIDENTIALITY_LEVELS = ['STANDARD', 'CONFIDENTIAL', 'HIGHLY_CONFIDENTIAL', 'ATTORNEY_CLIENT'];
const BILLING_TYPES = ['HOURLY', 'FLAT_FEE', 'RETAINER', 'CONTINGENCY', 'PRO_BONO'];

// Collapsible Section Component
const Section = ({ title, icon: Icon, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors"
      >
        <div className="flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 text-slate-500" />}
          <span className="font-medium text-slate-700">{title}</span>
        </div>
        {isOpen ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
      </button>
      {isOpen && <div className="p-4 space-y-4">{children}</div>}
    </div>
  );
};

// Form Field Component
const FormField = ({ label, required, children, hint }) => (
  <div className="space-y-1.5">
    <Label className="text-sm font-medium text-slate-700">
      {label} {required && <span className="text-red-500">*</span>}
    </Label>
    {children}
    {hint && <p className="text-xs text-slate-500">{hint}</p>}
  </div>
);

const CaseCreateModal = ({ 
  open, 
  onOpenChange, 
  clients = [], 
  investigators = [],
  token,
  onSuccess,
  asPage = false,
}) => {
  const navigate = useNavigate();
  const handleClose = () => {
    if (asPage) navigate('/cases');
    else onOpenChange && onOpenChange(false);
  };
  const [mode, setMode] = useState('quick'); // 'quick' or 'advanced'
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // State for adding subjects, vehicles, and locations during case creation
  const [subjects, setSubjects] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [locations, setLocations] = useState([]);
  
  const [formData, setFormData] = useState({
    // Basic Information
    title: '',
    case_type: 'SURVEILLANCE',
    case_sub_type: '',
    priority: 'NORMAL',
    risk_level: 'MEDIUM',
    confidentiality_level: 'STANDARD',
    tags: '',
    
    // Client Information
    client_id: '',
    client_contact_person: '',
    client_phone: '',
    client_email: '',
    referral_source: '',
    billing_type: 'HOURLY',
    hourly_rate: '',
    budget_cap: '',
    
    // Investigation Details
    description: '',
    detailed_narrative: '',
    objective: '',
    allegation_type: '',
    jurisdiction: '',
    county: '',
    state: '',
    state_name: '',
    city: '',
    latitude: null,
    longitude: null,
    related_court: '',
    external_case_number: '',
    warrant_related: false,
    case_classification: 'CIVIL',
    
    // Assignment Details
    primary_investigator: '',
    secondary_investigator: '',
    supervisor: '',
    department: '',
    due_date: '',
    target_completion_date: '',
    
    // Operational Planning
    surveillance_needed: false,
    interview_needed: false,
    field_work_needed: false,
    skip_trace_needed: false,
    records_pull_needed: false,
    evidence_collection_needed: false,
    background_needed: false,
    
    // Legal/Compliance
    is_confidential: false,
    attorney_client_related: false,
    law_enforcement_coordination: false,
    protective_order_related: false,
    consent_received: false,
    
    // Internal Controls
    internal_notes: '',
    restrict_by_role: false,
    restrict_by_assignment: true,
    notify_team_on_create: true,
    create_initial_timeline: true,
    
    // Case-Type Specific Fields
    // Surveillance
    surveillance_objective: '',
    target_schedule: '',
    known_routines: '',
    surveillance_start_date: '',
    surveillance_end_date: '',
    surveillance_times: '',
    field_instructions: '',
    safety_concerns: '',
    
    // Missing Person
    last_seen_date: '',
    last_seen_time: '',
    last_seen_location: '',
    clothing_description: '',
    missing_risk_level: 'MEDIUM',
    medical_concerns: '',
    emergency_contact: '',
    possible_destinations: '',
    
    // Bail Bond / Apprehension
    bond_company: '',
    bond_amount: '',
    warrant_type: '',
    court_name: '',
    warrant_issue_date: '',
    fugitive_risk_level: 'MEDIUM',
    last_known_address: '',
    known_associates: '',
    caution_indicators: '',
    extradition_limits: '',
    
    // Background Check
    records_scope: '',
    search_jurisdictions: '',
    subject_type: 'INDIVIDUAL',
    employer_organization: '',
    search_purpose: '',
    records_deadline: '',
  });

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.title) {
      toast({ title: 'Error', description: 'Case title is required', variant: 'destructive' });
      return;
    }
    if (!formData.client_id) {
      toast({ title: 'Error', description: 'Client is required', variant: 'destructive' });
      return;
    }

    setIsSubmitting(true);
    try {
      // Build payload - only include non-empty fields
      const payload = {
        title: formData.title,
        case_type: formData.case_type,
        client_id: formData.client_id,
        priority: formData.priority,
        description: formData.description || formData.detailed_narrative,
      };

      // Add optional fields if provided
      if (formData.due_date) payload.due_date = formData.due_date;
      if (formData.primary_investigator) payload.assigned_investigator_id = formData.primary_investigator;
      if (formData.billing_type) payload.billing_type = formData.billing_type;
      if (formData.hourly_rate) payload.hourly_rate = parseFloat(formData.hourly_rate);
      if (formData.risk_level) payload.risk_level = formData.risk_level;
      if (formData.jurisdiction) payload.jurisdiction = formData.jurisdiction;
      if (formData.objective) payload.objective = formData.objective;
      
      // Add case-type specific fields as metadata
      const metadata = {};
      if (formData.case_type === 'SURVEILLANCE') {
        if (formData.surveillance_objective) metadata.surveillance_objective = formData.surveillance_objective;
        if (formData.target_schedule) metadata.target_schedule = formData.target_schedule;
        if (formData.safety_concerns) metadata.safety_concerns = formData.safety_concerns;
      }
      if (formData.case_type === 'MISSING_PERSON') {
        if (formData.last_seen_date) metadata.last_seen_date = formData.last_seen_date;
        if (formData.last_seen_location) metadata.last_seen_location = formData.last_seen_location;
        if (formData.medical_concerns) metadata.medical_concerns = formData.medical_concerns;
      }
      if (formData.case_type === 'BAIL_BOND_APPREHENSION') {
        if (formData.bond_company) metadata.bond_company = formData.bond_company;
        if (formData.bond_amount) metadata.bond_amount = formData.bond_amount;
        if (formData.warrant_type) metadata.warrant_type = formData.warrant_type;
      }
      if (Object.keys(metadata).length > 0) {
        payload.metadata = metadata;
      }

      // Add operational flags
      payload.operational_flags = {
        surveillance_needed: formData.surveillance_needed,
        interview_needed: formData.interview_needed,
        field_work_needed: formData.field_work_needed,
        skip_trace_needed: formData.skip_trace_needed,
        records_pull_needed: formData.records_pull_needed,
        evidence_collection_needed: formData.evidence_collection_needed,
        background_needed: formData.background_needed,
      };

      const res = await fetch(`${BACKEND_URL}/api/cases`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        const data = await res.json();
        const caseId = data.id;
        
        // Create subjects if any
        for (const subject of subjects) {
          if (subject.first_name || subject.last_name) {
            try {
              await fetch(`${BACKEND_URL}/api/cases/${caseId}/subjects`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({
                  ...subject,
                  name: `${subject.first_name || ''} ${subject.last_name || ''}`.trim()
                })
              });
            } catch (e) { console.error('Failed to create subject:', e); }
          }
        }
        
        // Create vehicles if any
        for (const vehicle of vehicles) {
          if (vehicle.license_plate || vehicle.make) {
            try {
              await fetch(`${BACKEND_URL}/api/cases/${caseId}/vehicles`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify(vehicle)
              });
            } catch (e) { console.error('Failed to create vehicle:', e); }
          }
        }
        
        // Create locations if any
        for (const location of locations) {
          if (location.name || location.address) {
            try {
              await fetch(`${BACKEND_URL}/api/cases/${caseId}/locations`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({
                  ...location,
                  location_type: location.type
                })
              });
            } catch (e) { console.error('Failed to create location:', e); }
          }
        }
        
        toast({ title: 'Success', description: `Case ${data.case_number} created successfully` });
        onSuccess?.(data);
        handleClose();
        
        // Reset form and related arrays
        setSubjects([]);
        setVehicles([]);
        setLocations([]);
        setFormData({
          title: '', case_type: 'SURVEILLANCE', case_sub_type: '', priority: 'NORMAL',
          risk_level: 'MEDIUM', confidentiality_level: 'STANDARD', tags: '',
          client_id: '', client_contact_person: '', client_phone: '', client_email: '',
          referral_source: '', billing_type: 'HOURLY', hourly_rate: '', budget_cap: '',
          description: '', detailed_narrative: '', objective: '', allegation_type: '',
          jurisdiction: '', county: '', state: '', state_name: '', city: '', latitude: null, longitude: null, related_court: '', external_case_number: '',
          warrant_related: false, case_classification: 'CIVIL',
          primary_investigator: '', secondary_investigator: '', supervisor: '',
          department: '', due_date: '', target_completion_date: '',
          surveillance_needed: false, interview_needed: false, field_work_needed: false,
          skip_trace_needed: false, records_pull_needed: false, evidence_collection_needed: false,
          background_needed: false, is_confidential: false, attorney_client_related: false,
          law_enforcement_coordination: false, protective_order_related: false, consent_received: false,
          internal_notes: '', restrict_by_role: false, restrict_by_assignment: true,
          notify_team_on_create: true, create_initial_timeline: true,
          surveillance_objective: '', target_schedule: '', known_routines: '',
          surveillance_start_date: '', surveillance_end_date: '', surveillance_times: '',
          field_instructions: '', safety_concerns: '',
          last_seen_date: '', last_seen_time: '', last_seen_location: '', clothing_description: '',
          missing_risk_level: 'MEDIUM', medical_concerns: '', emergency_contact: '', possible_destinations: '',
          bond_company: '', bond_amount: '', warrant_type: '', court_name: '', warrant_issue_date: '',
          fugitive_risk_level: 'MEDIUM', last_known_address: '', known_associates: '',
          caution_indicators: '', extradition_limits: '',
          records_scope: '', search_jurisdictions: '', subject_type: 'INDIVIDUAL',
          employer_organization: '', search_purpose: '', records_deadline: '',
        });
      } else {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to create case');
      }
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render case-type specific fields
  const renderCaseTypeFields = () => {
    switch (formData.case_type) {
      case 'SURVEILLANCE':
        return (
          <Section title="Surveillance Details" icon={MapPin}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField label="Surveillance Objective">
                <Textarea
                  value={formData.surveillance_objective}
                  onChange={e => updateField('surveillance_objective', e.target.value)}
                  placeholder="What is the goal of the surveillance?"
                  className="border-slate-300"
                  rows={2}
                />
              </FormField>
              <FormField label="Target Schedule / Routines">
                <Textarea
                  value={formData.target_schedule}
                  onChange={e => updateField('target_schedule', e.target.value)}
                  placeholder="Known schedule, work hours, routines..."
                  className="border-slate-300"
                  rows={2}
                />
              </FormField>
              <FormField label="Surveillance Start Date">
                <Input
                  type="date"
                  value={formData.surveillance_start_date}
                  onChange={e => updateField('surveillance_start_date', e.target.value)}
                  className="border-slate-300"
                />
              </FormField>
              <FormField label="Surveillance End Date">
                <Input
                  type="date"
                  value={formData.surveillance_end_date}
                  onChange={e => updateField('surveillance_end_date', e.target.value)}
                  className="border-slate-300"
                />
              </FormField>
              <FormField label="Recommended Times">
                <Input
                  value={formData.surveillance_times}
                  onChange={e => updateField('surveillance_times', e.target.value)}
                  placeholder="e.g., 6AM-8AM, 5PM-7PM"
                  className="border-slate-300"
                />
              </FormField>
              <FormField label="Safety Concerns">
                <Input
                  value={formData.safety_concerns}
                  onChange={e => updateField('safety_concerns', e.target.value)}
                  placeholder="Any safety concerns for investigators"
                  className="border-slate-300"
                />
              </FormField>
              <div className="col-span-2">
                <FormField label="Field Instructions">
                  <Textarea
                    value={formData.field_instructions}
                    onChange={e => updateField('field_instructions', e.target.value)}
                    placeholder="Special instructions for field investigators..."
                    className="border-slate-300"
                    rows={3}
                  />
                </FormField>
              </div>
            </div>
          </Section>
        );

      case 'MISSING_PERSON':
        return (
          <Section title="Missing Person Details" icon={AlertTriangle} defaultOpen={true}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField label="Last Seen Date" required>
                <Input
                  type="date"
                  value={formData.last_seen_date}
                  onChange={e => updateField('last_seen_date', e.target.value)}
                  className="border-slate-300"
                />
              </FormField>
              <FormField label="Last Seen Time">
                <Input
                  type="time"
                  value={formData.last_seen_time}
                  onChange={e => updateField('last_seen_time', e.target.value)}
                  className="border-slate-300"
                />
              </FormField>
              <div className="col-span-2">
                <FormField label="Last Seen Location" required>
                  <Input
                    value={formData.last_seen_location}
                    onChange={e => updateField('last_seen_location', e.target.value)}
                    placeholder="Address or location description"
                    className="border-slate-300"
                  />
                </FormField>
              </div>
              <FormField label="Clothing Description">
                <Textarea
                  value={formData.clothing_description}
                  onChange={e => updateField('clothing_description', e.target.value)}
                  placeholder="What were they wearing?"
                  className="border-slate-300"
                  rows={2}
                />
              </FormField>
              <FormField label="Missing Risk Level">
                <Select value={formData.missing_risk_level} onValueChange={v => updateField('missing_risk_level', v)}>
                  <SelectTrigger className="border-slate-300"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {RISK_LEVELS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </FormField>
              <FormField label="Medical Concerns">
                <Textarea
                  value={formData.medical_concerns}
                  onChange={e => updateField('medical_concerns', e.target.value)}
                  placeholder="Medical conditions, medications needed..."
                  className="border-slate-300"
                  rows={2}
                />
              </FormField>
              <FormField label="Emergency Contact">
                <Input
                  value={formData.emergency_contact}
                  onChange={e => updateField('emergency_contact', e.target.value)}
                  placeholder="Name and phone number"
                  className="border-slate-300"
                />
              </FormField>
              <div className="col-span-2">
                <FormField label="Possible Destinations">
                  <Textarea
                    value={formData.possible_destinations}
                    onChange={e => updateField('possible_destinations', e.target.value)}
                    placeholder="Known friends, family, frequented locations..."
                    className="border-slate-300"
                    rows={2}
                  />
                </FormField>
              </div>
            </div>
          </Section>
        );

      case 'BAIL_BOND_APPREHENSION':
        return (
          <Section title="Bail Bond / Warrant Details" icon={Shield} defaultOpen={true}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField label="Bond Company">
                <Input
                  value={formData.bond_company}
                  onChange={e => updateField('bond_company', e.target.value)}
                  placeholder="Bail bond company name"
                  className="border-slate-300"
                />
              </FormField>
              <FormField label="Bond Amount">
                <Input
                  value={formData.bond_amount}
                  onChange={e => updateField('bond_amount', e.target.value)}
                  placeholder="$50,000"
                  className="border-slate-300"
                />
              </FormField>
              <FormField label="Warrant Type">
                <Select value={formData.warrant_type} onValueChange={v => updateField('warrant_type', v)}>
                  <SelectTrigger className="border-slate-300"><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="FAILURE_TO_APPEAR">Failure to Appear</SelectItem>
                    <SelectItem value="BOND_FORFEITURE">Bond Forfeiture</SelectItem>
                    <SelectItem value="BENCH_WARRANT">Bench Warrant</SelectItem>
                    <SelectItem value="ARREST_WARRANT">Arrest Warrant</SelectItem>
                  </SelectContent>
                </Select>
              </FormField>
              <FormField label="Court Name">
                <Input
                  value={formData.court_name}
                  onChange={e => updateField('court_name', e.target.value)}
                  placeholder="Issuing court"
                  className="border-slate-300"
                />
              </FormField>
              <FormField label="Warrant Issue Date">
                <Input
                  type="date"
                  value={formData.warrant_issue_date}
                  onChange={e => updateField('warrant_issue_date', e.target.value)}
                  className="border-slate-300"
                />
              </FormField>
              <FormField label="Fugitive Risk Level">
                <Select value={formData.fugitive_risk_level} onValueChange={v => updateField('fugitive_risk_level', v)}>
                  <SelectTrigger className="border-slate-300"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {RISK_LEVELS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </FormField>
              <div className="col-span-2">
                <FormField label="Last Known Address">
                  <Input
                    value={formData.last_known_address}
                    onChange={e => updateField('last_known_address', e.target.value)}
                    placeholder="Last known address"
                    className="border-slate-300"
                  />
                </FormField>
              </div>
              <FormField label="Known Associates">
                <Textarea
                  value={formData.known_associates}
                  onChange={e => updateField('known_associates', e.target.value)}
                  placeholder="Names, relationships, contact info..."
                  className="border-slate-300"
                  rows={2}
                />
              </FormField>
              <FormField label="Caution Indicators">
                <Textarea
                  value={formData.caution_indicators}
                  onChange={e => updateField('caution_indicators', e.target.value)}
                  placeholder="Armed, violent history, gang affiliations..."
                  className="border-slate-300"
                  rows={2}
                />
              </FormField>
              <div className="col-span-2">
                <FormField label="Extradition Limits">
                  <Input
                    value={formData.extradition_limits}
                    onChange={e => updateField('extradition_limits', e.target.value)}
                    placeholder="e.g., Continental US only, specific states..."
                    className="border-slate-300"
                  />
                </FormField>
              </div>
            </div>
          </Section>
        );

      case 'BACKGROUND_CHECK':
        return (
          <Section title="Background Check Details" icon={FileText}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField label="Records Scope">
                <Select value={formData.records_scope} onValueChange={v => updateField('records_scope', v)}>
                  <SelectTrigger className="border-slate-300"><SelectValue placeholder="Select scope" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="BASIC">Basic (Criminal, Identity)</SelectItem>
                    <SelectItem value="STANDARD">Standard (+ Employment, Education)</SelectItem>
                    <SelectItem value="COMPREHENSIVE">Comprehensive (+ Financial, Civil)</SelectItem>
                    <SelectItem value="DEEP">Deep Investigation</SelectItem>
                  </SelectContent>
                </Select>
              </FormField>
              <FormField label="Subject Type">
                <Select value={formData.subject_type} onValueChange={v => updateField('subject_type', v)}>
                  <SelectTrigger className="border-slate-300"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="INDIVIDUAL">Individual</SelectItem>
                    <SelectItem value="BUSINESS">Business Entity</SelectItem>
                  </SelectContent>
                </Select>
              </FormField>
              <FormField label="Search Jurisdictions">
                <Input
                  value={formData.search_jurisdictions}
                  onChange={e => updateField('search_jurisdictions', e.target.value)}
                  placeholder="e.g., Nationwide, CA, TX, FL"
                  className="border-slate-300"
                />
              </FormField>
              <FormField label="Search Purpose">
                <Select value={formData.search_purpose} onValueChange={v => updateField('search_purpose', v)}>
                  <SelectTrigger className="border-slate-300"><SelectValue placeholder="Select purpose" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="EMPLOYMENT">Employment</SelectItem>
                    <SelectItem value="TENANT">Tenant Screening</SelectItem>
                    <SelectItem value="BUSINESS">Business Due Diligence</SelectItem>
                    <SelectItem value="PERSONAL">Personal</SelectItem>
                    <SelectItem value="LEGAL">Legal Proceeding</SelectItem>
                  </SelectContent>
                </Select>
              </FormField>
              <FormField label="Employer / Organization">
                <Input
                  value={formData.employer_organization}
                  onChange={e => updateField('employer_organization', e.target.value)}
                  placeholder="If employment-related"
                  className="border-slate-300"
                />
              </FormField>
              <FormField label="Deadline">
                <Input
                  type="date"
                  value={formData.records_deadline}
                  onChange={e => updateField('records_deadline', e.target.value)}
                  className="border-slate-300"
                />
              </FormField>
            </div>
          </Section>
        );

      default:
        return null;
    }
  };

  // Shared form body — used by both modal and full-page renders.
  const FormBody = (
    <>
      <div className="flex-1 overflow-y-auto py-4 space-y-6 px-4 md:px-2">
          {mode === 'quick' ? (
            /* Quick Create Mode */
            <div className="space-y-4">
              <FormField label="Case Title" required>
                <Input
                  value={formData.title}
                  onChange={e => updateField('title', e.target.value)}
                  placeholder="Enter a descriptive case title"
                  className="border-slate-300"
                  data-testid="case-title-input"
                />
              </FormField>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField label="Case Type" required>
                  <Select value={formData.case_type} onValueChange={v => updateField('case_type', v)}>
                    <SelectTrigger className="border-slate-300" data-testid="case-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CASE_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </FormField>

                <FormField label="Client" required>
                  <Select value={formData.client_id} onValueChange={v => updateField('client_id', v)}>
                    <SelectTrigger className="border-slate-300" data-testid="client-select">
                      <SelectValue placeholder="Select client" />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </FormField>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <FormField label="Priority">
                  <Select value={formData.priority} onValueChange={v => updateField('priority', v)}>
                    <SelectTrigger className="border-slate-300"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PRIORITY_OPTIONS.map(p => (
                        <SelectItem key={p.value} value={p.value}>
                          <div className="flex items-center gap-2">
                            <span className={`w-2 h-2 rounded-full ${p.color.split(' ')[0]}`} />
                            {p.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormField>

                <FormField label="Primary Investigator">
                  <Select value={formData.primary_investigator} onValueChange={v => updateField('primary_investigator', v)}>
                    <SelectTrigger className="border-slate-300"><SelectValue placeholder="Select investigator" /></SelectTrigger>
                    <SelectContent>
                      {investigators.map(i => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </FormField>

                <FormField label="Due Date">
                  <Input
                    type="date"
                    value={formData.due_date}
                    onChange={e => updateField('due_date', e.target.value)}
                    className="border-slate-300"
                  />
                </FormField>
              </div>

              <FormField label="Description">
                <Textarea
                  value={formData.description}
                  onChange={e => updateField('description', e.target.value)}
                  placeholder="Brief case description..."
                  className="border-slate-300"
                  rows={3}
                />
              </FormField>
            </div>
          ) : (
            /* Advanced Create Mode */
            <div className="space-y-4">
              {/* Basic Information Section */}
              <Section title="Basic Information" icon={FileText} defaultOpen={true}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <FormField label="Case Title" required>
                      <Input
                        value={formData.title}
                        onChange={e => updateField('title', e.target.value)}
                        placeholder="Enter a descriptive case title"
                        className="border-slate-300"
                      />
                    </FormField>
                  </div>
                  <FormField label="Case Type" required>
                    <Select value={formData.case_type} onValueChange={v => updateField('case_type', v)}>
                      <SelectTrigger className="border-slate-300"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CASE_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </FormField>
                  {CASE_SUB_TYPES[formData.case_type] && (
                    <FormField label="Sub-Type">
                      <Select value={formData.case_sub_type} onValueChange={v => updateField('case_sub_type', v)}>
                        <SelectTrigger className="border-slate-300"><SelectValue placeholder="Select sub-type" /></SelectTrigger>
                        <SelectContent>
                          {CASE_SUB_TYPES[formData.case_type].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </FormField>
                  )}
                  <FormField label="Priority">
                    <Select value={formData.priority} onValueChange={v => updateField('priority', v)}>
                      <SelectTrigger className="border-slate-300"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {PRIORITY_OPTIONS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </FormField>
                  <FormField label="Risk Level">
                    <Select value={formData.risk_level} onValueChange={v => updateField('risk_level', v)}>
                      <SelectTrigger className="border-slate-300"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {RISK_LEVELS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </FormField>
                  <FormField label="Confidentiality Level">
                    <Select value={formData.confidentiality_level} onValueChange={v => updateField('confidentiality_level', v)}>
                      <SelectTrigger className="border-slate-300"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CONFIDENTIALITY_LEVELS.map(c => <SelectItem key={c} value={c}>{c.replace(/_/g, ' ')}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </FormField>
                  <FormField label="Tags / Keywords">
                    <Input
                      value={formData.tags}
                      onChange={e => updateField('tags', e.target.value)}
                      placeholder="Comma-separated tags"
                      className="border-slate-300"
                    />
                  </FormField>
                </div>
              </Section>

              {/* Client Information */}
              <Section title="Client Information" icon={Building}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField label="Client" required>
                    <Select value={formData.client_id} onValueChange={v => updateField('client_id', v)}>
                      <SelectTrigger className="border-slate-300"><SelectValue placeholder="Select client" /></SelectTrigger>
                      <SelectContent>
                        {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </FormField>
                  <FormField label="Client Contact Person">
                    <Input
                      value={formData.client_contact_person}
                      onChange={e => updateField('client_contact_person', e.target.value)}
                      placeholder="Point of contact name"
                      className="border-slate-300"
                    />
                  </FormField>
                  <FormField label="Contact Phone">
                    <Input
                      value={formData.client_phone}
                      onChange={e => updateField('client_phone', e.target.value)}
                      placeholder="Phone number"
                      className="border-slate-300"
                    />
                  </FormField>
                  <FormField label="Contact Email">
                    <Input
                      type="email"
                      value={formData.client_email}
                      onChange={e => updateField('client_email', e.target.value)}
                      placeholder="Email address"
                      className="border-slate-300"
                    />
                  </FormField>
                  <FormField label="Referral Source">
                    <Input
                      value={formData.referral_source}
                      onChange={e => updateField('referral_source', e.target.value)}
                      placeholder="How did they find us?"
                      className="border-slate-300"
                    />
                  </FormField>
                  <FormField label="Billing Type">
                    <Select value={formData.billing_type} onValueChange={v => updateField('billing_type', v)}>
                      <SelectTrigger className="border-slate-300"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {BILLING_TYPES.map(b => <SelectItem key={b} value={b}>{b.replace(/_/g, ' ')}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </FormField>
                  <FormField label="Hourly Rate">
                    <Input
                      type="number"
                      value={formData.hourly_rate}
                      onChange={e => updateField('hourly_rate', e.target.value)}
                      placeholder="$/hour"
                      className="border-slate-300"
                    />
                  </FormField>
                  <FormField label="Budget Cap">
                    <Input
                      type="number"
                      value={formData.budget_cap}
                      onChange={e => updateField('budget_cap', e.target.value)}
                      placeholder="Maximum budget"
                      className="border-slate-300"
                    />
                  </FormField>
                </div>
              </Section>

              {/* Investigation Details */}
              <Section title="Investigation Details" icon={FileText}>
                <div className="space-y-4">
                  <FormField label="Case Summary" required>
                    <Textarea
                      value={formData.description}
                      onChange={e => updateField('description', e.target.value)}
                      placeholder="Brief summary of the case..."
                      className="border-slate-300"
                      rows={2}
                    />
                  </FormField>
                  <FormField label="Detailed Narrative / Intake Notes">
                    <Textarea
                      value={formData.detailed_narrative}
                      onChange={e => updateField('detailed_narrative', e.target.value)}
                      placeholder="Full intake notes and case background..."
                      className="border-slate-300"
                      rows={4}
                    />
                  </FormField>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField label="Objective / Mission">
                      <Input
                        value={formData.objective}
                        onChange={e => updateField('objective', e.target.value)}
                        placeholder="Primary case objective"
                        className="border-slate-300"
                      />
                    </FormField>
                    <FormField label="Jurisdiction">
                      <Input
                        value={formData.jurisdiction}
                        onChange={e => updateField('jurisdiction', e.target.value)}
                        placeholder="City, County, State..."
                        className="border-slate-300"
                      />
                    </FormField>
                    {/* Iter124 — County Assignment cascading dropdowns
                        (State → City → County). Also captures lat/lng so
                        the case appears as a pin on the dashboard map. */}
                    <div className="col-span-2">
                      <FormField label="County Assignment">
                        <CountyAssignmentPicker
                          value={{ state: formData.state, city: formData.city, county: formData.county }}
                          onChange={v => {
                            updateField('state', v.state);
                            updateField('state_name', v.state_name);
                            updateField('city', v.city);
                            updateField('county', v.county);
                            updateField('latitude', v.latitude);
                            updateField('longitude', v.longitude);
                          }}
                        />
                      </FormField>
                    </div>
                    <FormField label="Related Court / Agency">
                      <Input
                        value={formData.related_court}
                        onChange={e => updateField('related_court', e.target.value)}
                        placeholder="Court or agency name"
                        className="border-slate-300"
                      />
                    </FormField>
                    <FormField label="External Case Number">
                      <Input
                        value={formData.external_case_number}
                        onChange={e => updateField('external_case_number', e.target.value)}
                        placeholder="Docket number, police report #, etc."
                        className="border-slate-300"
                      />
                    </FormField>
                  </div>
                </div>
              </Section>

              {/* Assignment Details */}
              <Section title="Assignment Details" icon={User}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField label="Primary Investigator">
                    <Select
                      value={formData.primary_investigator || '__none__'}
                      onValueChange={v => updateField('primary_investigator', v === '__none__' ? '' : v)}
                    >
                      <SelectTrigger className="border-slate-300"><SelectValue placeholder="Select investigator" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">None</SelectItem>
                        {(investigators || []).map(i => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </FormField>
                  <FormField label="Secondary Investigator">
                    <Select
                      value={formData.secondary_investigator || '__none__'}
                      onValueChange={v => updateField('secondary_investigator', v === '__none__' ? '' : v)}
                    >
                      <SelectTrigger className="border-slate-300"><SelectValue placeholder="Optional" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">None</SelectItem>
                        {(investigators || []).map(i => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </FormField>
                  <FormField label="Due Date">
                    <Input
                      type="date"
                      value={formData.due_date || ''}
                      onChange={e => updateField('due_date', e.target.value)}
                      className="border-slate-300"
                    />
                  </FormField>
                  <FormField label="Target Completion">
                    <Input
                      type="date"
                      value={formData.target_completion_date || ''}
                      onChange={e => updateField('target_completion_date', e.target.value)}
                      className="border-slate-300"
                    />
                  </FormField>
                </div>
              </Section>

              {/* Operational Planning */}
              <Section title="Operational Planning" icon={Calendar}>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { key: 'surveillance_needed', label: 'Surveillance' },
                    { key: 'interview_needed', label: 'Interviews' },
                    { key: 'field_work_needed', label: 'Field Work' },
                    { key: 'skip_trace_needed', label: 'Skip Trace' },
                    { key: 'records_pull_needed', label: 'Records Pull' },
                    { key: 'evidence_collection_needed', label: 'Evidence Collection' },
                    { key: 'background_needed', label: 'Background Check' },
                  ].map(item => (
                    <label key={item.key} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={formData[item.key]}
                        onCheckedChange={checked => updateField(item.key, checked)}
                      />
                      <span className="text-sm text-slate-700">{item.label}</span>
                    </label>
                  ))}
                </div>
              </Section>

              {/* Case-Type Specific Fields */}
              {renderCaseTypeFields()}

              {/* Subjects Section - Add multiple subjects during case creation */}
              <Section title="Subjects of Interest" icon={User}>
                <div className="space-y-3">
                  {subjects.map((subject, idx) => (
                    <div key={idx} className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                      <div className="flex items-start justify-between mb-2">
                        <span className="text-sm font-medium text-slate-700">Subject {idx + 1}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setSubjects(prev => prev.filter((_, i) => i !== idx))}
                          className="text-red-500 h-6 w-6 p-0"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        <Input
                          placeholder="First Name"
                          value={subject.first_name || ''}
                          onChange={e => {
                            const updated = [...subjects];
                            updated[idx] = { ...updated[idx], first_name: e.target.value };
                            setSubjects(updated);
                          }}
                          className="border-slate-300 text-sm"
                        />
                        <Input
                          placeholder="Last Name"
                          value={subject.last_name || ''}
                          onChange={e => {
                            const updated = [...subjects];
                            updated[idx] = { ...updated[idx], last_name: e.target.value };
                            setSubjects(updated);
                          }}
                          className="border-slate-300 text-sm"
                        />
                        <Input
                          placeholder="Alias / Nickname"
                          value={subject.alias || ''}
                          onChange={e => {
                            const updated = [...subjects];
                            updated[idx] = { ...updated[idx], alias: e.target.value };
                            setSubjects(updated);
                          }}
                          className="border-slate-300 text-sm"
                        />
                        <Input
                          placeholder="Phone"
                          value={subject.phone || ''}
                          onChange={e => {
                            const updated = [...subjects];
                            updated[idx] = { ...updated[idx], phone: e.target.value };
                            setSubjects(updated);
                          }}
                          className="border-slate-300 text-sm"
                        />
                        <Input
                          placeholder="Email"
                          value={subject.email || ''}
                          onChange={e => {
                            const updated = [...subjects];
                            updated[idx] = { ...updated[idx], email: e.target.value };
                            setSubjects(updated);
                          }}
                          className="border-slate-300 text-sm"
                        />
                        <Select
                          value={subject.relationship || ''}
                          onValueChange={v => {
                            const updated = [...subjects];
                            updated[idx] = { ...updated[idx], relationship: v };
                            setSubjects(updated);
                          }}
                        >
                          <SelectTrigger className="border-slate-300 text-sm"><SelectValue placeholder="Relationship" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="target">Target</SelectItem>
                            <SelectItem value="witness">Witness</SelectItem>
                            <SelectItem value="associate">Associate</SelectItem>
                            <SelectItem value="defendant">Defendant</SelectItem>
                            <SelectItem value="plaintiff">Plaintiff</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setSubjects(prev => [...prev, { first_name: '', last_name: '', relationship: 'target' }])}
                    className="w-full border-dashed"
                  >
                    <Plus className="w-3 h-3 mr-1" /> Add Subject
                  </Button>
                </div>
              </Section>

              {/* Vehicles Section - Add multiple vehicles during case creation */}
              <Section title="Vehicles of Interest" icon={Car}>
                <div className="space-y-3">
                  {vehicles.map((vehicle, idx) => (
                    <div key={idx} className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                      <div className="flex items-start justify-between mb-2">
                        <span className="text-sm font-medium text-slate-700">Vehicle {idx + 1}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setVehicles(prev => prev.filter((_, i) => i !== idx))}
                          className="text-red-500 h-6 w-6 p-0"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        <Input
                          placeholder="License Plate"
                          value={vehicle.license_plate || ''}
                          onChange={e => {
                            const updated = [...vehicles];
                            updated[idx] = { ...updated[idx], license_plate: e.target.value };
                            setVehicles(updated);
                          }}
                          className="border-slate-300 text-sm"
                        />
                        <Input
                          placeholder="Make"
                          value={vehicle.make || ''}
                          onChange={e => {
                            const updated = [...vehicles];
                            updated[idx] = { ...updated[idx], make: e.target.value };
                            setVehicles(updated);
                          }}
                          className="border-slate-300 text-sm"
                        />
                        <Input
                          placeholder="Model"
                          value={vehicle.model || ''}
                          onChange={e => {
                            const updated = [...vehicles];
                            updated[idx] = { ...updated[idx], model: e.target.value };
                            setVehicles(updated);
                          }}
                          className="border-slate-300 text-sm"
                        />
                        <Input
                          placeholder="Year"
                          value={vehicle.year || ''}
                          onChange={e => {
                            const updated = [...vehicles];
                            updated[idx] = { ...updated[idx], year: e.target.value };
                            setVehicles(updated);
                          }}
                          className="border-slate-300 text-sm"
                        />
                        <Input
                          placeholder="Color"
                          value={vehicle.color || ''}
                          onChange={e => {
                            const updated = [...vehicles];
                            updated[idx] = { ...updated[idx], color: e.target.value };
                            setVehicles(updated);
                          }}
                          className="border-slate-300 text-sm"
                        />
                        <Input
                          placeholder="VIN"
                          value={vehicle.vin || ''}
                          onChange={e => {
                            const updated = [...vehicles];
                            updated[idx] = { ...updated[idx], vin: e.target.value };
                            setVehicles(updated);
                          }}
                          className="border-slate-300 text-sm"
                        />
                      </div>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setVehicles(prev => [...prev, { license_plate: '', make: '', model: '' }])}
                    className="w-full border-dashed"
                  >
                    <Plus className="w-3 h-3 mr-1" /> Add Vehicle
                  </Button>
                </div>
              </Section>

              {/* Locations Section - Add multiple locations during case creation */}
              <Section title="Locations of Interest" icon={MapPin}>
                <div className="space-y-3">
                  {locations.map((location, idx) => (
                    <div key={idx} className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                      <div className="flex items-start justify-between mb-2">
                        <span className="text-sm font-medium text-slate-700">Location {idx + 1}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setLocations(prev => prev.filter((_, i) => i !== idx))}
                          className="text-red-500 h-6 w-6 p-0"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        <Input
                          placeholder="Location Name / Label"
                          value={location.name || ''}
                          onChange={e => {
                            const updated = [...locations];
                            updated[idx] = { ...updated[idx], name: e.target.value };
                            setLocations(updated);
                          }}
                          className="border-slate-300 text-sm"
                        />
                        <Select
                          value={location.type || ''}
                          onValueChange={v => {
                            const updated = [...locations];
                            updated[idx] = { ...updated[idx], type: v };
                            setLocations(updated);
                          }}
                        >
                          <SelectTrigger className="border-slate-300 text-sm"><SelectValue placeholder="Location Type" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="residence">Residence</SelectItem>
                            <SelectItem value="workplace">Workplace</SelectItem>
                            <SelectItem value="surveillance">Surveillance Point</SelectItem>
                            <SelectItem value="poi">Point of Interest</SelectItem>
                            <SelectItem value="last_known">Last Known Location</SelectItem>
                          </SelectContent>
                        </Select>
                        <Input
                          placeholder="Address"
                          value={location.address || ''}
                          onChange={e => {
                            const updated = [...locations];
                            updated[idx] = { ...updated[idx], address: e.target.value };
                            setLocations(updated);
                          }}
                          className="border-slate-300 text-sm col-span-2"
                        />
                        <Textarea
                          placeholder="Notes about this location..."
                          value={location.notes || ''}
                          onChange={e => {
                            const updated = [...locations];
                            updated[idx] = { ...updated[idx], notes: e.target.value };
                            setLocations(updated);
                          }}
                          className="border-slate-300 text-sm col-span-2"
                          rows={2}
                        />
                      </div>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setLocations(prev => [...prev, { name: '', type: 'poi', address: '' }])}
                    className="w-full border-dashed"
                  >
                    <Plus className="w-3 h-3 mr-1" /> Add Location
                  </Button>
                </div>
              </Section>

              {/* Internal Notes */}
              <Section title="Internal Notes" icon={StickyNote}>
                <FormField label="Internal Notes (Admin Only)">
                  <Textarea
                    value={formData.internal_notes}
                    onChange={e => updateField('internal_notes', e.target.value)}
                    placeholder="Notes for internal use only..."
                    className="border-slate-300"
                    rows={3}
                  />
                </FormField>
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={formData.notify_team_on_create}
                      onCheckedChange={checked => updateField('notify_team_on_create', checked)}
                    />
                    <span className="text-sm text-slate-700">Notify team on create</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={formData.create_initial_timeline}
                      onCheckedChange={checked => updateField('create_initial_timeline', checked)}
                    />
                    <span className="text-sm text-slate-700">Create initial timeline entry</span>
                  </label>
                </div>
              </Section>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="flex-shrink-0 pt-4 border-t border-slate-200 flex items-center justify-between px-4 md:px-2">
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => toast({ title: 'Draft saved', description: 'Case draft has been saved' })}
            >
              Save Draft
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="bg-[#0F172A] hover:bg-[#1E293B] text-white"
              data-testid="submit-case-btn"
            >
              {isSubmitting ? 'Creating...' : 'Create Case'}
            </Button>
          </div>
        </div>
    </>
  );

  // Header (mode-switcher) — shared by both renders
  const Header = (
    <div className="flex items-center justify-between flex-wrap gap-2">
      <div className="text-xl font-bold text-slate-900">Create New Investigation Case</div>
      <div className="flex items-center gap-2">
        <Button
          variant={mode === 'quick' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setMode('quick')}
          className={mode === 'quick' ? 'bg-[#0F172A]' : ''}
        >
          <Zap className="w-4 h-4 mr-1" /> Quick
        </Button>
        <Button
          variant={mode === 'advanced' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setMode('advanced')}
          className={mode === 'advanced' ? 'bg-[#0F172A]' : ''}
        >
          <FileText className="w-4 h-4 mr-1" /> Advanced
        </Button>
      </div>
    </div>
  );

  if (asPage) {
    return (
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm" data-testid="case-create-page-wrapper" style={{ minHeight: 'calc(100vh - 200px)' }}>
        <div className="px-5 pt-4 pb-3 border-b border-slate-200">
          {Header}
        </div>
        <div className="p-2 md:p-4 text-slate-900">
          {FormBody}
        </div>
      </div>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="flex-shrink-0 pb-4 border-b border-slate-200">
          <DialogTitle className="sr-only">Create Case</DialogTitle>
          {Header}
        </DialogHeader>
        {FormBody}
      </DialogContent>
    </Dialog>
  );
};

export default CaseCreateModal;
